import React, { useState, useMemo, useEffect, useCallback } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { IoIosArrowDropdown } from "react-icons/io";
import { RiResetLeftLine } from "react-icons/ri";
import Loading from "../../../Extra/Loading";
import { getCurrentFY, getFinancialYearMonthsPassed } from "../../../Utilitie";

const monthsRef = [
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
];

const allow = (set, value) => set.size === 0 || set.has(value);

const LTSummaryController = ({
  setFilteredAF,
  openStates,
  setOpenStates,
  setAFData,
}) => {
  // ================= STATE =================
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [searchValues, setSearchValues] = useState({
    Year: "",
    detention_grp: "",
    group_code: "",
    sub_group_code: "",
    Month: "",
    section: "",
    trainNo: "",
  });

  const [selectedFY, setSelectedFY] = useState(new Set());
  const [selectedDetentionGroup, setSelectedDetentionGroup] = useState(
    new Set(),
  );
  const [selectedgroup_code, setSelectedgroup_code] = useState(new Set());
  const [selectedsubGroupCode, setSelectedsubGroupCode] = useState(new Set());
  const [selectedMonths, setSelectedMonths] = useState(new Set());
  const [selectedCoasection, setSelectedCoasection] = useState(new Set());
  const [selectedTrainNo, setSelectedTrainNo] = useState(new Set());

  // ================= FETCH =================
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    const url = `${process.env.REACT_APP_URL_PREFIX_CRIS}getDetentionByFY?fy`;

    try {
      const res = await fetch(url);
      const json = await res.json();
      const merged = [...json.currentData, ...json.prevData];
      setData(merged);
      setAFData(merged);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [setAFData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  const a =
    data.filter((o) => o.F_YEAR === "2025-26") &&
    data.filter((o) => o.F_YEAR === "2024-25");

  const filteredData = useMemo(
    () =>
      data.filter(
        (d) =>
          allow(selectedFY, d.F_YEAR) &&
          allow(selectedDetentionGroup, d.detention_grp) &&
          allow(selectedgroup_code, d.group_code) &&
          allow(selectedsubGroupCode, d.sub_group_code) &&
          allow(selectedMonths, d.A_MONTH) &&
          allow(selectedCoasection, d.section) &&
          allow(selectedTrainNo, d.trainNo),
      ),
    [
      data,
      selectedFY,
      selectedDetentionGroup,
      selectedgroup_code,
      selectedsubGroupCode,
      selectedMonths,
      selectedCoasection,
      selectedTrainNo,
    ],
  );

  useEffect(() => {
    setFilteredAF(filteredData);
  }, [filteredData, setFilteredAF]);

  // ================= AVAILABLE OPTIONS =================
  const availableFY = useMemo(
    () => [...new Set(data.map((d) => d.F_YEAR))],
    [data],
  );

  const availableDetentionGroup = useMemo(
    () => [
      ...new Set(
        data
          .filter((d) => allow(selectedFY, d.F_YEAR))
          .map((d) => d.detention_grp),
      ),
    ],
    [data, selectedFY],
  );

  const availablegroup_code = useMemo(
    () => [
      ...new Set(
        data
          .filter(
            (d) =>
              allow(selectedFY, d.F_YEAR) &&
              allow(selectedDetentionGroup, d.detention_grp),
          )
          .map((d) => d.group_code),
      ),
    ],
    [data, selectedFY, selectedDetentionGroup],
  );

  const availablesubGroupCode = useMemo(
    () => [
      ...new Set(
        data
          .filter(
            (d) =>
              allow(selectedFY, d.F_YEAR) &&
              allow(selectedDetentionGroup, d.detention_grp) &&
              allow(selectedgroup_code, d.group_code),
          )
          .map((d) => d.sub_group_code),
      ),
    ],
    [data, selectedFY, selectedDetentionGroup, selectedgroup_code],
  );

  const availableMonths = useMemo(() => {
    const m = [
      ...new Set(
        data
          .filter(
            (d) =>
              allow(selectedFY, d.F_YEAR) &&
              allow(selectedDetentionGroup, d.detention_grp) &&
              allow(selectedgroup_code, d.group_code) &&
              allow(selectedsubGroupCode, d.sub_group_code),
          )
          .map((d) => d.A_MONTH),
      ),
    ];
    return m.sort((a, b) => monthsRef.indexOf(a) - monthsRef.indexOf(b));
  }, [
    data,
    selectedFY,
    selectedDetentionGroup,
    selectedgroup_code,
    selectedsubGroupCode,
  ]);

  const availableCoasection = useMemo(
    () => [
      ...new Set(
        data
          .filter(
            (d) =>
              allow(selectedFY, d.F_YEAR) &&
              allow(selectedDetentionGroup, d.detention_grp) &&
              allow(selectedgroup_code, d.group_code) &&
              allow(selectedsubGroupCode, d.sub_group_code) &&
              allow(selectedMonths, d.A_MONTH),
          )
          .map((d) => d.section),
      ),
    ],
    [
      data,
      selectedFY,
      selectedDetentionGroup,
      selectedgroup_code,
      selectedsubGroupCode,
      selectedMonths,
    ],
  );

  const availableTrainNo = useMemo(
    () => [
      ...new Set(
        data
          .filter(
            (d) =>
              allow(selectedFY, d.F_YEAR) &&
              allow(selectedDetentionGroup, d.detention_grp) &&
              allow(selectedgroup_code, d.group_code) &&
              allow(selectedsubGroupCode, d.sub_group_code) &&
              allow(selectedMonths, d.A_MONTH) &&
              allow(selectedCoasection, d.section),
          )
          .map((d) => d.trainNo),
      ),
    ],
    [
      data,
      selectedFY,
      selectedDetentionGroup,
      selectedgroup_code,
      selectedsubGroupCode,
      selectedMonths,
      selectedCoasection,
    ],
  );

  // ================= DEFAULT SETUP =================
  const defaultSetup = () => {
    setSelectedFY(new Set(availableFY));
    setSelectedDetentionGroup(new Set(availableDetentionGroup));
    setSelectedgroup_code(new Set(availablegroup_code));
    setSelectedsubGroupCode(new Set(availablesubGroupCode));
    setSelectedMonths(getFinancialYearMonthsPassed(getCurrentFY(), false));
    setSelectedCoasection(new Set(availableCoasection));
    setSelectedTrainNo(new Set(availableTrainNo));

    // 🔥 reset dropdown open states
    setOpenStates({
      Year: false,
      detention_grp: false,
      group_code: false,
      sub_group_code: false,
      Month: false,
      section: false,
      trainNo: false,
    });
  };

  useEffect(() => {
    if (data.length) defaultSetup();
  }, [data]);

  // ================= UI HELPERS =================
  const toggleSelection = (setFunc, set, value) => {
    const n = new Set(set);
    n.has(value) ? n.delete(value) : n.add(value);
    setFunc(n);
  };

  const toggleDropdown = (title) => {
    setOpenStates((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleSearchChange = (title, value) => {
    setSearchValues((prev) => ({
      ...prev,
      [title]: value,
    }));
  };

  const renderCheckboxList = (title, items, selectedSet, setFunc) => {
    const filteredItems = items.filter((i) =>
      i?.toString().toLowerCase().includes(searchValues[title].toLowerCase()),
    );

    return (
      <div className="col-md mt-2 p-1">
        <div
          className="bg-light"
          style={{ position: "relative", fontSize: 12 }}
        >
          <div
            className="newUiBody"
            onClick={() => toggleDropdown(title)}
            style={{
              border: "1px solid #ccc",
              padding: "8px",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            Select {title}
            <IoIosArrowDropdown
              style={{
                float: "right",
                transition: "0.3s",
                transform: openStates[title]
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              }}
            />
          </div>

          {openStates[title] && (
            <div
              className="newUiBody"
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 10,
                background: "#fff",
                border: "1px solid #ccc",
              }}
            >
              <input
                className="form-control mb-1"
                placeholder={`Search ${title}`}
                value={searchValues[title]}
                onChange={(e) => handleSearchChange(title, e.target.value)}
              />

              <div style={{ maxHeight: 250, overflowY: "auto" }}>
                {filteredItems.map((item) => (
                  <label
                    key={item}
                    className="d-block ms-2 d-flex justify-content-start"
                  >
                    <input
                      type="checkbox"
                      className="me-1"
                      checked={selectedSet.has(item)}
                      onChange={() =>
                        toggleSelection(setFunc, selectedSet, item)
                      }
                    />
                    {item}
                  </label>
                ))}
              </div>

              <hr className="m-1" />

              <div className="d-flex justify-content-evenly">
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setFunc(new Set(items))}
                >
                  Select All
                </button>
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={() => setFunc(new Set())}
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ================= UI =================
  return (
    <div className="newUiBody m-3">
      {loading && <Loading />}

      <div className="row">
        {renderCheckboxList("Year", availableFY, selectedFY, setSelectedFY)}
        {renderCheckboxList(
          "detention_grp",
          availableDetentionGroup,
          selectedDetentionGroup,
          setSelectedDetentionGroup,
        )}
        {renderCheckboxList(
          "group_code",
          availablegroup_code,
          selectedgroup_code,
          setSelectedgroup_code,
        )}
        {renderCheckboxList(
          "sub_group_code",
          availablesubGroupCode,
          selectedsubGroupCode,
          setSelectedsubGroupCode,
        )}
        {renderCheckboxList(
          "Month",
          availableMonths,
          selectedMonths,
          setSelectedMonths,
        )}
        {renderCheckboxList(
          "section",
          availableCoasection,
          selectedCoasection,
          setSelectedCoasection,
        )}
        {renderCheckboxList(
          "trainNo",
          availableTrainNo,
          selectedTrainNo,
          setSelectedTrainNo,
        )}

        <OverlayTrigger overlay={<Tooltip>Reset Filter</Tooltip>}>
          <div className="btn btn-sm btn-danger mt-2" onClick={defaultSetup}>
            <RiResetLeftLine />
          </div>
        </OverlayTrigger>
      </div>
    </div>
  );
};

export default LTSummaryController;
